import { Bot, Star, User } from 'lucide-react';

import { cn } from '../../lib/utils';

/**
 * User-type discriminator.
 *
 * - `HI`  — Human Identity (rendered with the lucide `User` glyph).
 * - `NHI` — Non-Human Identity / service account (rendered with the lucide
 *   `Bot` glyph).
 */
export type UserTypeBadgeType = 'HI' | 'NHI';

/**
 * Size variant for the badge.
 *
 * - `'md'` (default) — 20×20 tile with 16-px glyph. Bit-for-bit identical to
 *   the pre-size-prop behavior (D2 frozen-contract for `TopIdentitiesByFileAccessBody`).
 * - `'sm'` — 18×18 tile with 12-px glyph. Matches the `AccessLegitimacyGrid`
 *   cell layout (Figma inline `#FFF5E6` privileged tile).
 */
export type UserTypeBadgeSizeVariant = 'sm' | 'md';

export interface UserTypeBadgeProps {
  /** Identity type discriminator. See {@link UserTypeBadgeType}. */
  type: UserTypeBadgeType;
  /**
   * When `true`, an additional orange-tinted tile is rendered to the right of
   * the type tile, containing a 12-px Star glyph (Figma `5:23634` privileged-user
   * marker palette `#FFCA27`). Default: `false`.
   */
  isPrivileged?: boolean;
  /**
   * Visual size variant. Default: `'md'` (20×20 tile, 16-px glyph, bg-orange-100
   * privileged tile — unchanged from v1). `'sm'` renders 18×18 tile, 12-px glyph,
   * inline `#FFF5E6` privileged tile (matches `AccessLegitimacyGrid` cell).
   */
  size?: UserTypeBadgeSizeVariant;
  /**
   * Optional accessible label. When supplied, the wrapping span receives
   * `role="img"` and `aria-label`; both inner tiles stay `aria-hidden`. When
   * omitted, the entire badge is decorative (`aria-hidden`) — the consuming
   * row should carry the announcement instead.
   */
  'aria-label'?: string;
  className?: string;
}

// `Star` is rendered with `fill="currentColor"` so the inline color flows
// through both the stroke and the fill. `dawn-yellow` is NOT defined in
// `packages/ui/tailwind.config.ts` extend.colors as of TDEV-169 Phase 1; the
// closest token is `category.4` (#FFCA27 — same hex). We deliberately use an
// inline `style={{ color: ... }}` plus `data-color-token="dawn-yellow"` so the
// intent is greppable when the token is added to the Tailwind config.
// TODO(TDEV-273 follow-up): introduce `dawn-yellow` as a Tailwind token (or
// migrate to `text-category-4`) and replace the inline style + data attribute.
const PRIVILEGED_STAR_COLOR = '#FFCA27' as const;

// `sm` privileged tile uses inline `#FFF5E6` (18px Figma `AccessLegitimacyGrid`
// cell background) instead of the `bg-orange-100` Tailwind class used by `md`.
// Same inline-style migration anchor pattern as PRIVILEGED_STAR_COLOR above.
const SM_PRIVILEGED_BG = '#FFF5E6' as const;

const TYPE_TO_GLYPH = {
  HI: User,
  NHI: Bot,
} as const satisfies Readonly<Record<UserTypeBadgeType, typeof User>>;

/**
 * Tile size (Tailwind className) keyed by size variant.
 * `md` preserves the v1 `h-5 w-5` (20px) shape; `sm` uses bracketed `h-[18px] w-[18px]` (18px).
 */
const SIZE_TILE = {
  md: 'h-5 w-5',
  sm: 'h-[18px] w-[18px]',
} as const satisfies Readonly<Record<UserTypeBadgeSizeVariant, string>>;

/**
 * Glyph size (px) keyed by size variant.
 * `md` = 16px (v1); `sm` = 12px (AccessLegitimacyGrid cell).
 */
const SIZE_GLYPH = {
  md: 16,
  sm: 12,
} as const satisfies Readonly<Record<UserTypeBadgeSizeVariant, number>>;

/**
 * `UserTypeBadge` — grey-100 tile carrying a lucide identity glyph (`User` for
 * `HI`, `Bot` for `NHI`), with an optional adjacent orange-tinted tile
 * containing a 12-px Star glyph for privileged identities.
 *
 * **Size variants:**
 * - `size='md'` (default) — 20×20 tile, 16-px glyph, `bg-orange-100`
 *   privileged tile. Bit-for-bit identical to the pre-size-prop v1 render
 *   (D2 frozen-contract for `TopIdentitiesByFileAccessBody`).
 * - `size='sm'` — 18×18 tile, 12-px glyph, inline `#FFF5E6` privileged tile
 *   (matches `AccessLegitimacyGrid` cell layout, Figma inline palette).
 *
 * Used by the Top Identities by File Access widget (Figma `5:23634`) and the
 * Access Legitimacy Grid. Composes `lucide-react` glyphs directly (deliberately
 * does NOT depend on `UserTypeIcon` so that the `HI`/`NHI` API enum maps
 * cleanly without an `internal`/`external`/`serviceAccount` translation layer).
 */
export function UserTypeBadge({
  type,
  isPrivileged = false,
  size = 'md',
  'aria-label': ariaLabel,
  className,
}: UserTypeBadgeProps) {
  const Glyph = TYPE_TO_GLYPH[type];
  const tileSize = SIZE_TILE[size];
  const glyphSize = SIZE_GLYPH[size];

  return (
    <span
      className={cn('inline-flex items-center gap-1', className)}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
    >
      <span
        data-testid={`user-type-badge-tile-${type === 'HI' ? 'hi' : 'nhi'}`}
        aria-hidden="true"
        className={cn(
          'inline-flex items-center justify-center rounded-[2px] bg-grey-100',
          tileSize
        )}
      >
        <Glyph width={glyphSize} height={glyphSize} />
      </span>
      {isPrivileged ? (
        <span
          data-testid="user-type-badge-tile-privileged"
          aria-hidden="true"
          className={cn(
            'inline-flex items-center justify-center rounded-[2px]',
            tileSize,
            size === 'md' ? 'bg-orange-100' : undefined
          )}
          style={size === 'sm' ? { backgroundColor: SM_PRIVILEGED_BG } : undefined}
        >
          <Star
            width={12}
            height={12}
            fill="currentColor"
            data-color-token="dawn-yellow"
            style={{ color: PRIVILEGED_STAR_COLOR }}
          />
        </span>
      ) : null}
    </span>
  );
}

UserTypeBadge.displayName = 'UserTypeBadge';
